/**
 * Hard spaces (U+00A0) for every text source — one adapter, four hook points.
 *
 * Every Strapi read goes through `ContentGateway.read()` (LIVE and the DEMO
 * fallback alike), which hands its result here. Strapi and the seed keep plain
 * spaces; the hard space is added at render time by `@no-human/typografia`.
 *
 * This is `hardSpacesDeep` with two refinements the content shape needs:
 * - rich-text fields (`body`, `html`) go through `hardSpacesHtml`, which touches
 *   text nodes only — never tags, attributes, `<script>` or `<code>`;
 * - identifier-like fields stay verbatim, because they are compared, put in URLs
 *   or posted back (tag/stream filters, option values, the search query,
 *   JSON-LD).
 *
 * The same adapter also typesets the non-CMS text sources: the Polish UI
 * dictionary (core/copy.ts) and the content seed the views read directly
 * (data/seed.ts), once at export, and the frozen @mb-group/i18n chrome at the
 * one place every `ctx.t()` passes through (main.ts). Together with the gateway
 * these are the only hook points. Text leaving the page as data goes back
 * through `plainText()`.
 * Polish uses `typeset('pl')` semantics (orphans + spaced dashes); English is
 * returned unchanged, so `a` and `I` never get bound.
 */
import type { Locale } from "@mb-group/shared-types";
import { NBSP, hardSpaces, hardSpacesHtml, type HardSpacesOptions } from "@no-human/typografia";

/** Rich-text (trusted HTML) fields. */
const HTML_KEYS = new Set(["body", "html"]);

/**
 * Fields used as identifiers or data, not prose: compared, put in URLs, posted
 * back or machine-read. Exported so a test pins every one of them.
 *
 * Only fields whose values CONTAIN SPACES need to be here — the package changes
 * nothing but a single space, so slugs, hrefs, ids, keys and e-mails are safe
 * by definition. A new field with spaces that goes into a URL, an API call or a
 * comparison → add it here (and to the literal list in typography-pages.test.ts).
 */
export const VERBATIM_KEYS: ReadonlySet<string> = new Set([
	"tags",
	"materialStreams",
	"query",
	"suggestions",
	"structuredData",
	"tokenOverrides",
]);

/**
 * `value` is verbatim only inside `options` (a form option's value is posted
 * back); elsewhere it is display text — a stat or metric value ("Bez żargonu").
 */
export const VERBATIM_IN: Readonly<Record<string, string>> = { value: "options" };

function verbatim(key: string, parentKey: string | undefined): boolean {
	return (
		VERBATIM_KEYS.has(key) || (VERBATIM_IN[key] !== undefined && VERBATIM_IN[key] === parentKey)
	);
}

const PL: HardSpacesOptions = { lang: "pl", rules: { dashes: true } };

/**
 * Source object -> its typeset copy. Keeps the object graph's shape (the DEMO
 * dataset is cyclic: service -> category -> services) and lets the memoised
 * DEMO data be typeset once instead of on every read. CMS content is never
 * mutated after it is read, so a cached copy cannot go stale.
 */
const copies = new WeakMap<object, unknown>();

function walk(value: unknown, html: boolean, parentKey?: string): unknown {
	if (typeof value === "string") return html ? hardSpacesHtml(value, PL) : hardSpaces(value, PL);
	if (value === null || typeof value !== "object") return value;
	// Plain JSON only (like hardSpacesDeep): a Date, Map or class instance passes
	// through untouched instead of collapsing into `{}`.
	if (!Array.isArray(value) && Object.getPrototypeOf(value) !== Object.prototype) return value;
	const known = copies.get(value);
	if (known !== undefined) return known;
	if (Array.isArray(value)) {
		const out: unknown[] = [];
		copies.set(value, out);
		// Items of an array belong to the array's key (`options: [{ value }]`).
		for (const item of value) out.push(walk(item, html, parentKey));
		return out;
	}
	const out: Record<string, unknown> = {};
	copies.set(value, out);
	for (const [key, item] of Object.entries(value)) {
		out[key] = verbatim(key, parentKey) ? item : walk(item, HTML_KEYS.has(key), key);
	}
	return out;
}

/** Return a copy of CMS content with hard spaces for `locale`; input is not modified. */
export function typesetContent<T>(value: T, locale: Locale): T {
	if (locale !== "pl") return value;
	return walk(value, false) as T;
}

/**
 * The reverse, for text that leaves the page as data: a search query typed,
 * clicked from a typeset prompt chip or pasted from the page. Analytics, state
 * and the search API see plain spaces, exactly as if the words were typed.
 */
export function plainText(value: string): string {
	return value.split(NBSP).join(" ");
}
